/**
 * Structured Data Builders — Restore.Dental
 * 
 * Helper functions to generate schema.org JSON-LD structured data.
 * All functions return objects ready for injection into <SEO> component.
 * 
 * IMPORTANT RULES:
 * - Only mark up content that's VISIBLE on the page
 * - Keep answers ≤ 350 characters (Google truncates in SERP)
 * - Don't use FAQ schema on transactional pages (booking, thank-you)
 * 
 * @see https://schema.org/FAQPage
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */

import { PUBLIC_SITE_URL, BRAND_LOGO_IMAGE } from './seo';
import { PRACTICE, getSameAsLinks, getOpeningHoursSpecification, getPostalAddress, getGeo } from './practice';

const DR_PATEL_IMAGE = `${PUBLIC_SITE_URL}/dr-kartik-patel.jpg`;
const ABOUT_URL = `${PUBLIC_SITE_URL}/about-dr-kartik-patel`;

// ============================================================================
// FAQ PAGE SCHEMA
// ============================================================================

/**
 * Generate FAQPage structured data
 * 
 * Use this for any page with visible FAQ sections.
 * 
 * @param {Array} faqs - Array of FAQ objects with question & answer
 * @param {string} faqs[].question - The question text
 * @param {string} faqs[].answer - The answer text (≤350 chars recommended)
 * 
 * @returns {object} FAQPage schema object
 * 
 * @example
 * const faqSchema = getFAQPageLD([
 *   {
 *     question: "Do you offer payment plans?",
 *     answer: "Yes! We offer 0% interest-free finance from 3-60 months..."
 *   },
 *   {
 *     question: "How long does Invisalign take?",
 *     answer: "Treatment typically takes 6-18 months depending on..."
 *   }
 * ]);
 */
export const getFAQPageLD = (faqs) => {
  if (!faqs || faqs.length === 0) return null;

  // Validate and truncate answers if needed
  const validatedFAQs = faqs.map(faq => {
    let answer = faq.answer || '';
    
    // Warn if answer exceeds 350 chars (dev only)
    if (process.env.NODE_ENV === 'development' && answer.length > 350) {
      console.warn(
        `⚠️ FAQ Schema: Answer exceeds 350 chars (${answer.length} chars):\n"${faq.question}"`
      );
    }
    
    return {
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': answer,
      }
    };
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': validatedFAQs,
  };
};

// ============================================================================
// ORGANIZATION SCHEMA
// ============================================================================

/**
 * Generate Organization structured data
 * 
 * Use this on homepage and main pages to define your business entity.
 * Helps Google build Knowledge Panel.
 * 
 * @returns {object} Organization schema object
 */
export const getOrganizationLD = () => {
  return {
    '@context': 'https://schema.org',
    '@type': ['Organization', 'Dentist', 'MedicalBusiness'],
    '@id': `${PUBLIC_SITE_URL}/#organization`,
    'name': PRACTICE.name,
    'alternateName': PRACTICE.alternateName,
    'url': PUBLIC_SITE_URL,
    'logo': {
      '@type': 'ImageObject',
      'url': BRAND_LOGO_IMAGE,
      'width': 800,
      'height': 800,
    },
    'image': BRAND_LOGO_IMAGE,
    'description': 'Private dental practice at 64a Horn Lane, Acton, West London, led by Dr Kartik Patel BDS (GDC 177993). Invisalign, dental implants, composite bonding, cosmetic and restorative dentistry with 0% finance.',
    'slogan': PRACTICE.tagline,
    'priceRange': '££',
    'telephone': PRACTICE.phoneE164,
    'email': PRACTICE.email,
    'address': getPostalAddress(),
    'geo': getGeo(),
    'hasMap': PRACTICE.links.googleMaps,
    'openingHoursSpecification': getOpeningHoursSpecification(),
    'areaServed': ['Acton', 'Ealing', 'Chiswick', 'Hammersmith', 'Shepherd\'s Bush', 'Kensington', 'Notting Hill', 'Richmond', 'Brentford', 'Gunnersbury', 'West London'],
    'founder': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
    'employee': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': PRACTICE.phoneE164,
      'contactType': 'reservations',
      'areaServed': 'GB',
      'availableLanguage': ['English'],
    },
    'sameAs': getSameAsLinks(),
  };
};

// ============================================================================
// WEBSITE SCHEMA (with Sitelinks Search Box)
// ============================================================================

/**
 * Generate WebSite structured data with SearchAction
 * 
 * Enables Google to show a sitelinks search box in SERPs.
 * Users can search your site directly from Google results.
 * 
 * @returns {object} WebSite schema object
 */
export const getWebSiteLD = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${PUBLIC_SITE_URL}/#website`,
    'name': 'Restore.Dental',
    'url': PUBLIC_SITE_URL,
    'potentialAction': {
      '@type': 'SearchAction',
      'target': {
        '@type': 'EntryPoint',
        'urlTemplate': `${PUBLIC_SITE_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };
};

// ============================================================================
// DENTIST CLINIC SCHEMA (Detailed)
// ============================================================================

/**
 * Generate detailed Dentist/MedicalBusiness structured data
 * 
 * More detailed than Organization schema, focuses on medical practice.
 * Include opening hours, medical specialties, ratings.
 * 
 * @param {object} options - Optional overrides
 * @param {object} options.aggregateRating - Rating data { value, count, best }
 * 
 * @returns {object} Dentist schema object
 */
export const getDentistClinicLD = (options = {}) => {
  const baseSchema = {
    '@context': 'https://schema.org',
    '@type': 'Dentist',
    '@id': `${PUBLIC_SITE_URL}/#dentist`,
    'name': PRACTICE.name,
    'alternateName': PRACTICE.alternateName,
    'image': [BRAND_LOGO_IMAGE, DR_PATEL_IMAGE],
    'logo': BRAND_LOGO_IMAGE,
    'url': PUBLIC_SITE_URL,
    'telephone': PRACTICE.phoneE164,
    'email': PRACTICE.email,
    'priceRange': '££',
    'currenciesAccepted': 'GBP',
    'paymentAccepted': 'Cash, Credit Card, Debit Card, 0% Finance (Tabeo)',
    'address': getPostalAddress(),
    'geo': getGeo(),
    'hasMap': PRACTICE.links.googleMaps,
    'openingHoursSpecification': getOpeningHoursSpecification(),
    'sameAs': getSameAsLinks(),
    'parentOrganization': { '@id': `${PUBLIC_SITE_URL}/#organization` },
    'employee': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
    'isAcceptingNewPatients': true,
    'availableService': [
      { '@type': 'MedicalProcedure', 'name': 'Invisalign Clear Aligners', 'url': `${PUBLIC_SITE_URL}/invisalign-acton` },
      { '@type': 'MedicalProcedure', 'name': 'Dental Implants', 'url': `${PUBLIC_SITE_URL}/implants-acton` },
      { '@type': 'MedicalProcedure', 'name': 'Composite Bonding', 'url': `${PUBLIC_SITE_URL}/composite-bonding-acton` },
      { '@type': 'MedicalProcedure', 'name': 'Porcelain Veneers', 'url': `${PUBLIC_SITE_URL}/services/porcelain-veneers` },
      { '@type': 'MedicalProcedure', 'name': 'Teeth Whitening', 'url': `${PUBLIC_SITE_URL}/services/teeth-whitening` },
      { '@type': 'MedicalProcedure', 'name': 'Emergency Dentistry', 'url': `${PUBLIC_SITE_URL}/emergency-dentist-acton` },
      { '@type': 'MedicalProcedure', 'name': 'All-on-4 Implants', 'url': `${PUBLIC_SITE_URL}/services/all-on-4-implants` },
      { '@type': 'MedicalProcedure', 'name': 'TMJ & Bruxism Treatment', 'url': `${PUBLIC_SITE_URL}/services/tmj-dentistry` },
      { '@type': 'MedicalProcedure', 'name': 'Sedation Dentistry', 'url': `${PUBLIC_SITE_URL}/services/sedation` },
      { '@type': 'MedicalProcedure', 'name': 'General & Preventive Dentistry', 'url': `${PUBLIC_SITE_URL}/dentist-acton` },
    ],
    'medicalSpecialty': [
      'Cosmetic Dentistry',
      'Restorative Dentistry',
      'Orthodontics',
      'Prosthodontics',
      'Periodontics',
      'Endodontics',
    ],
  };

  // Add aggregate rating if provided
  if (options.aggregateRating) {
    baseSchema.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': options.aggregateRating.value.toString(),
      'reviewCount': options.aggregateRating.count.toString(),
      'bestRating': (options.aggregateRating.best || 5).toString(),
    };
  }

  return baseSchema;
};

// ============================================================================
// PERSON / CLINICIAN SCHEMA
// ============================================================================

/**
 * Generate Person/Physician structured data for clinicians
 * 
 * Use for dentists, hygienists, and other medical professionals.
 * 
 * @param {object} clinician - Clinician details
 * @param {string} clinician.name - Full name (e.g., "Dr. Kartik Patel")
 * @param {string} clinician.honorificPrefix - Title (e.g., "Dr.")
 * @param {string} clinician.honorificSuffix - Qualifications (e.g., "BDS, MJDF RCS(Eng)")
 * @param {string} clinician.jobTitle - Position (e.g., "Principal Dentist")
 * @param {string} clinician.description - Bio summary
 * @param {string} clinician.image - Photo URL
 * @param {Array} clinician.medicalSpecialty - Specialties
 * @param {Array} clinician.alumniOf - Dental schools
 * @param {Array} clinician.sameAs - Professional profiles
 * 
 * @returns {object} Person schema object
 */
export const getPersonClinicianLD = (clinician = {}) => {
  const c = { ...drKartikPatel, ...clinician };
  return {
    '@context': 'https://schema.org',
    '@type': ['Person', 'Physician', 'Dentist'],
    '@id': `${PUBLIC_SITE_URL}/#person-${c.name.toLowerCase().replace(/\s+/g, '-')}`,
    'name': c.name,
    'givenName': 'Kartik',
    'familyName': 'Patel',
    'honorificPrefix': c.honorificPrefix || 'Dr.',
    'honorificSuffix': c.honorificSuffix || '',
    'jobTitle': c.jobTitle || 'Dentist',
    'description': c.description || '',
    'image': c.image || DR_PATEL_IMAGE,
    'url': ABOUT_URL,
    'mainEntityOfPage': ABOUT_URL,
    'telephone': PRACTICE.phoneE164,
    'worksFor': {
      '@type': 'Organization',
      '@id': `${PUBLIC_SITE_URL}/#organization`,
      'name': PRACTICE.name,
      'url': PUBLIC_SITE_URL,
    },
    'workLocation': {
      '@type': 'Place',
      'name': PRACTICE.name,
      'address': getPostalAddress(),
    },
    'identifier': {
      '@type': 'PropertyValue',
      'propertyID': 'GDC Registration Number',
      'value': PRACTICE.gdcNumber,
      'url': PRACTICE.links.gdcRegister,
    },
    'hasCredential': [
      {
        '@type': 'EducationalOccupationalCredential',
        'credentialCategory': 'degree',
        'name': 'Bachelor of Dental Surgery (BDS)',
        'recognizedBy': { '@type': 'EducationalOrganization', 'name': 'King\'s College London' },
      },
      {
        '@type': 'EducationalOccupationalCredential',
        'credentialCategory': 'license',
        'name': `General Dental Council Registration ${PRACTICE.gdcNumber}`,
        'recognizedBy': { '@type': 'Organization', 'name': 'General Dental Council', 'url': 'https://www.gdc-uk.org' },
      },
    ],
    'alumniOf': c.alumniOf,
    'knowsAbout': c.medicalSpecialty,
    'medicalSpecialty': c.medicalSpecialty,
    'sameAs': c.sameAs,
  };
};

/**
 * Dr. Kartik Patel - Principal Dentist
 * Pre-configured clinician data
 */
export const drKartikPatel = {
  name: 'Dr. Kartik Patel',
  honorificPrefix: 'Dr.',
  honorificSuffix: 'BDS',
  jobTitle: 'Principal Dentist & Invisalign Provider',
  description: 'Principal dentist at Restore.Dental, Acton, West London. BDS (King\'s College London), GDC 177993. 15+ years\' experience, 150+ Invisalign cases, advanced training in dental implants, composite bonding, cosmetic and full-mouth restorative dentistry.',
  image: DR_PATEL_IMAGE,
  medicalSpecialty: [
    'Invisalign Clear Aligners',
    'Dental Implants',
    'Composite Bonding',
    'Cosmetic Dentistry',
    'Restorative Dentistry',
    'TMJ & Bruxism Treatment',
    'Sedation Dentistry',
  ],
  alumniOf: [
    {
      '@type': 'EducationalOrganization',
      'name': 'King\'s College London',
      'sameAs': 'https://www.kcl.ac.uk',
    },
  ],
  sameAs: [
    PRACTICE.links.gdcRegister,
    PRACTICE.links.instagram,
    PRACTICE.links.facebook,
  ],
};

// ============================================================================
// LOCAL BUSINESS SCHEMA (Original - Keep for Compatibility)
// ============================================================================

export const getLocalBusinessLD = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${PUBLIC_SITE_URL}/#localbusiness`,
    'name': PRACTICE.name,
    'image': BRAND_LOGO_IMAGE,
    'telephone': PRACTICE.phoneE164,
    'address': getPostalAddress(),
    'geo': getGeo(),
    'url': PUBLIC_SITE_URL,
    'priceRange': '££',
    'openingHoursSpecification': getOpeningHoursSpecification(),
    'sameAs': getSameAsLinks(),
  };
};

// ============================================================================
// OFFER CATALOG (Pricing page)
// ============================================================================

export const getOfferCatalogLD = (categories) => ({
  '@context': 'https://schema.org',
  '@type': 'Dentist',
  '@id': `${PUBLIC_SITE_URL}/#dentist`,
  'name': PRACTICE.name,
  'url': `${PUBLIC_SITE_URL}/pricing`,
  'address': getPostalAddress(),
  'telephone': PRACTICE.phoneE164,
  'hasOfferCatalog': {
    '@type': 'OfferCatalog',
    'name': 'Restore.Dental Fee Guide',
    'itemListElement': categories.map((cat) => ({
      '@type': 'OfferCatalog',
      'name': cat.title,
      'itemListElement': cat.items.map((item) => {
        const nums = (item.price.match(/[\d,]+/g) || []).map((n) => Number(n.replace(/,/g, '')));
        const offer = {
          '@type': 'Offer',
          'name': item.treatment,
          'url': `${PUBLIC_SITE_URL}${item.link}`,
          'priceCurrency': 'GBP',
          'itemOffered': { '@type': 'MedicalProcedure', 'name': item.treatment },
        };
        if (nums.length >= 2) {
          offer.priceSpecification = { '@type': 'PriceSpecification', 'minPrice': nums[0], 'maxPrice': nums[1], 'priceCurrency': 'GBP' };
        } else if (nums.length === 1) {
          offer.price = nums[0];
        } else {
          offer.price = 0;
        }
        return offer;
      }),
    })),
  },
});

// ============================================================================
// PROFILE PAGE (About Dr Patel)
// ============================================================================

export const getProfilePageLD = () => ({
  '@context': 'https://schema.org',
  '@type': 'ProfilePage',
  '@id': `${ABOUT_URL}#profilepage`,
  'url': ABOUT_URL,
  'name': 'About Dr Kartik Patel — Restore.Dental',
  'mainEntity': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
  'isPartOf': { '@id': `${PUBLIC_SITE_URL}/#website` },
});

export const getContactPageLD = () => ({
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  '@id': `${PUBLIC_SITE_URL}/contact#contactpage`,
  'url': `${PUBLIC_SITE_URL}/contact`,
  'name': 'Contact Restore.Dental, Acton',
  'mainEntity': { '@id': `${PUBLIC_SITE_URL}/#dentist` },
  'isPartOf': { '@id': `${PUBLIC_SITE_URL}/#website` },
});

// ============================================================================
// IMAGE GALLERY (Before & After)
// ============================================================================

export const getImageGalleryLD = (cases = []) => {
  const api = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    '@id': `${PUBLIC_SITE_URL}/before-after#gallery`,
    'url': `${PUBLIC_SITE_URL}/before-after`,
    'name': 'Restore.Dental Before & After Gallery',
    'description': 'Real before-and-after photographs of Restore.Dental patients in Acton, West London, published with consent.',
    'publisher': { '@id': `${PUBLIC_SITE_URL}/#organization` },
    'about': { '@id': `${PUBLIC_SITE_URL}/#dentist` },
    'associatedMedia': cases.flatMap((c) => [
      { '@type': 'ImageObject', 'name': `${c.title} — before`, 'caption': `${c.treatment}: before treatment. ${c.caption || ''}`.trim(), 'contentUrl': `${api}${c.before_url}`, 'width': c.width, 'height': c.height, 'representativeOfPage': false, 'copyrightHolder': { '@id': `${PUBLIC_SITE_URL}/#organization` } },
      { '@type': 'ImageObject', 'name': `${c.title} — after`, 'caption': `${c.treatment}: after treatment. ${c.caption || ''}`.trim(), 'contentUrl': `${api}${c.after_url}`, 'width': c.width, 'height': c.height, 'representativeOfPage': true, 'copyrightHolder': { '@id': `${PUBLIC_SITE_URL}/#organization` } },
    ]),
  };
};

// ============================================================================
// REVIEWS (Dentist + AggregateRating + Review[])
// ============================================================================

export const getReviewsLD = ({ rating, count, reviews = [] }) => ({
  '@context': 'https://schema.org',
  '@type': 'Dentist',
  '@id': `${PUBLIC_SITE_URL}/#dentist`,
  'name': PRACTICE.name,
  'url': `${PUBLIC_SITE_URL}/reviews`,
  'image': BRAND_LOGO_IMAGE,
  'telephone': PRACTICE.phoneE164,
  'address': getPostalAddress(),
  'aggregateRating': {
    '@type': 'AggregateRating',
    'ratingValue': String(rating),
    'reviewCount': String(count),
    'bestRating': '5',
    'worstRating': '1',
  },
  'review': reviews.slice(0, 25).map((r) => ({
    '@type': 'Review',
    'author': { '@type': 'Person', 'name': r.author },
    'datePublished': r.date ? String(r.date).slice(0, 10) : undefined,
    'reviewBody': r.text,
    'reviewRating': { '@type': 'Rating', 'ratingValue': String(r.rating), 'bestRating': '5', 'worstRating': '1' },
    'publisher': { '@type': 'Organization', 'name': 'Google' },
    'url': r.url || undefined,
  })),
});

// ============================================================================
// TREATMENT × AREA (programmatic local service pages)
// ============================================================================

export const getTreatmentAreaLD = ({ treatment, area, path, description }) => ({
  '@context': 'https://schema.org',
  '@type': ['MedicalProcedure', 'Service'],
  '@id': `${PUBLIC_SITE_URL}${path}#service`,
  'name': `${treatment.name} for ${area.name} patients`,
  'alternateName': `${treatment.name} ${area.name}`,
  'url': `${PUBLIC_SITE_URL}${path}`,
  'description': description || treatment.description(area),
  'procedureType': 'https://schema.org/NoninvasiveProcedure',
  'serviceType': treatment.name,
  'provider': { '@id': `${PUBLIC_SITE_URL}/#dentist` },
  'performer': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
  'areaServed': {
    '@type': 'Place',
    'name': `${area.name}, London ${area.postcode}`,
    'address': { '@type': 'PostalAddress', 'addressLocality': area.name, 'postalCode': area.postcode, 'addressRegion': 'London', 'addressCountry': 'GB' },
  },
  'availableChannel': {
    '@type': 'ServiceChannel',
    'serviceLocation': { '@type': 'Place', 'name': PRACTICE.name, 'address': getPostalAddress(), 'geo': getGeo() },
    'servicePhone': { '@type': 'ContactPoint', 'telephone': PRACTICE.phoneE164, 'contactType': 'reservations' },
  },
  'offers': {
    '@type': 'Offer',
    'priceCurrency': 'GBP',
    'price': Number((treatment.priceFrom.match(/[\d,]+/) || ['0'])[0].replace(/,/g, '')),
    'priceSpecification': { '@type': 'PriceSpecification', 'minPrice': Number((treatment.priceFrom.match(/[\d,]+/) || ['0'])[0].replace(/,/g, '')), 'priceCurrency': 'GBP' },
    'availability': 'https://schema.org/InStock',
    'url': `${PUBLIC_SITE_URL}${path}`,
  },
});

// ============================================================================
// BREADCRUMB SCHEMA
// ============================================================================

/**
 * Generate BreadcrumbList structured data
 * 
 * Use on inner pages to show navigation hierarchy in search results.
 * 
 * @param {Array} breadcrumbs - Array of breadcrumb objects
 * @param {string} breadcrumbs[].name - Display name
 * @param {string} breadcrumbs[].url - Full URL
 * 
 * @returns {object} BreadcrumbList schema object
 * 
 * @example
 * const breadcrumbSchema = getBreadcrumbLD([
 *   { name: 'Home', url: 'https://restore.dental/' },
 *   { name: 'Services', url: 'https://restore.dental/services' },
 *   { name: 'Invisalign', url: 'https://restore.dental/invisalign-acton' }
 * ]);
 */
export const getBreadcrumbLD = (breadcrumbs) => {
  if (!breadcrumbs || breadcrumbs.length === 0) return null;

  const itemListElement = breadcrumbs.map((crumb, index) => ({
    '@type': 'ListItem',
    'position': index + 1,
    'name': crumb.name,
    'item': crumb.url,
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': itemListElement,
  };
};

// ============================================================================
// MEDICAL CONDITION SCHEMA
// ============================================================================

/**
 * Generate MedicalCondition structured data
 * 
 * Describes the dental condition/problem that the service treats.
 * Helps Google understand what problems your services solve.
 * 
 * @param {object} condition - Condition details
 * @param {string} condition.name - Condition name (e.g., "Missing Teeth")
 * @param {Array} condition.signOrSymptom - Symptoms/signs
 * @param {string} condition.possibleTreatment - Link to procedure
 * @param {Array} condition.riskFactor - Risk factors
 * @param {string} condition.associatedAnatomy - Affected body part
 * 
 * @returns {object} MedicalCondition schema object
 * 
 * @example
 * const conditionSchema = getMedicalConditionLD({
 *   name: 'Missing Teeth',
 *   signOrSymptom: ['Gap in smile', 'Difficulty chewing', 'Bone loss'],
 *   possibleTreatment: 'https://restore.dental/implants-acton',
 *   riskFactor: ['Tooth decay', 'Gum disease', 'Trauma'],
 *   associatedAnatomy: 'Tooth'
 * });
 */
export const getMedicalConditionLD = (condition) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalCondition',
    'name': condition.name,
    'signOrSymptom': condition.signOrSymptom?.map(symptom => ({
      '@type': 'MedicalSymptom',
      'name': symptom,
    })) || [],
    'possibleTreatment': [
      {
        '@type': 'MedicalProcedure',
        'name': condition.treatmentName || condition.name + ' Treatment',
        'url': condition.possibleTreatment,
      }
    ],
    'riskFactor': condition.riskFactor?.map(factor => ({
      '@type': 'MedicalRiskFactor',
      'name': factor,
    })) || [],
    'associatedAnatomy': {
      '@type': 'AnatomicalStructure',
      'name': condition.associatedAnatomy || 'Tooth',
    },
  };
};

// ============================================================================
// MEDICAL PROCEDURE SCHEMA
// ============================================================================

/**
 * Generate MedicalProcedure structured data
 * 
 * Describes the dental procedure/treatment in detail.
 * Feeds Google's health topic panels and "In-depth" rich results.
 * 
 * @param {object} procedure - Procedure details
 * @param {string} procedure.name - Procedure name (e.g., "Dental Implant Placement")
 * @param {string} procedure.procedureType - Type (e.g., "Surgical", "Cosmetic")
 * @param {string} procedure.howPerformed - How it's done
 * @param {string} procedure.followup - Post-procedure care
 * @param {string} procedure.preparation - Pre-procedure steps
 * @param {string} procedure.bodyLocation - Area treated
 * @param {string} procedure.expectedPrognosis - Expected outcome
 * @param {string} procedure.url - Procedure page URL
 * 
 * @returns {object} MedicalProcedure schema object
 */
export const getMedicalProcedureLD = (procedure) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    'name': procedure.name,
    'alternateName': procedure.alternateName || [],
    'procedureType': procedure.procedureType,
    'url': procedure.url || PUBLIC_SITE_URL,
    'howPerformed': procedure.howPerformed,
    'followup': procedure.followup,
    'preparation': procedure.preparation,
    'bodyLocation': {
      '@type': 'AnatomicalStructure',
      'name': procedure.bodyLocation || 'Mouth',
    },
    'expectedPrognosis': procedure.expectedPrognosis,
    'performedBy': {
      '@type': 'Dentist',
      'name': 'Restore.Dental',
      'url': PUBLIC_SITE_URL,
    },
  };
};

// ============================================================================
// MEDICAL SERVICE SCHEMA (Original - Keep for Compatibility)
// ============================================================================

/**
 * Generate MedicalService structured data
 * 
 * Use on service pages (Invisalign, Implants, etc.)
 * 
 * @param {object} service - Service details
 * @param {string} service.name - Service name
 * @param {string} service.description - Service description
 * @param {string} service.url - Service page URL
 * 
 * @returns {object} MedicalService schema object
 */
export const getMedicalServiceLD = (service) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    'name': service.name,
    'description': service.description,
    'url': service.url,
    'provider': {
      '@type': 'Dentist',
      'name': 'Restore.Dental',
      'url': PUBLIC_SITE_URL,
    },
  };
};

// ============================================================================
// AGGREGATE RATING SCHEMA
// ============================================================================

/**
 * Generate AggregateRating structured data
 * 
 * Use when displaying review stars/ratings.
 * 
 * @param {object} rating - Rating data
 * @param {number} rating.value - Average rating (e.g., 4.9)
 * @param {number} rating.count - Number of reviews (e.g., 127)
 * @param {number} rating.best - Best possible rating (usually 5)
 * 
 * @returns {object} AggregateRating schema object
 */
export const getAggregateRatingLD = (rating) => {
  return {
    '@type': 'AggregateRating',
    'ratingValue': rating.value.toString(),
    'reviewCount': rating.count.toString(),
    'bestRating': (rating.best || 5).toString(),
  };
};

// ============================================================================
// SPEAKABLE SCHEMA (Voice Search Optimization)
// ============================================================================

/**
 * Generate Speakable structured data for voice search
 * 
 * Helps Google Assistant and other voice assistants identify
 * key content to read aloud in voice search results.
 * 
 * @param {Array<string>} speakableTexts - Array of text snippets optimized for voice
 * 
 * @returns {object} Speakable schema object
 * 
 * @example
 * const speakableSchema = getSpeakableLD([
 *   'Award-winning dentist in Acton',
 *   'Expert Invisalign provider',
 *   'Same-day emergency appointments available'
 * ]);
 */
export const getSpeakableLD = (speakableTexts) => {
  if (!speakableTexts || speakableTexts.length === 0) return null;

  // Return null for now - this schema is optional
  // Can be enhanced later if needed
  return null;
};

// ============================================================================
// MEDICAL WEB PAGE / ARTICLE SCHEMA
// ============================================================================

/**
 * Generate MedicalWebPage / Article structured data
 * 
 * Use for blog posts, knowledge base articles, and medical content pages.
 * 
 * @param {object} article - Article details
 * @param {string} article.headline - Article title
 * @param {string} article.description - Article summary
 * @param {string} article.url - Full article URL
 * @param {string} article.datePublished - ISO date string
 * @param {string} article.dateModified - ISO date string
 * @param {string} article.author - Author name
 * @param {string} article.image - Article image URL
 * 
 * @returns {object} Article schema object
 */
export const getMedicalWebPageLD = (article) => {
  return {
    '@context': 'https://schema.org',
    '@type': ['Article', 'MedicalWebPage'],
    '@id': article.url ? `${article.url}#article` : undefined,
    'mainEntityOfPage': article.url,
    'headline': article.headline,
    'description': article.description,
    'url': article.url,
    'inLanguage': 'en-GB',
    'datePublished': article.datePublished,
    'dateModified': article.dateModified || article.datePublished,
    'keywords': Array.isArray(article.keywords) ? article.keywords.join(', ') : article.keywords,
    'articleSection': article.articleSection,
    'wordCount': article.wordCount,
    'author': {
      '@type': 'Person',
      '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel`,
      'name': article.author || 'Dr. Kartik Patel',
      'honorificSuffix': 'BDS',
      'jobTitle': 'Principal Dentist',
      'url': ABOUT_URL,
    },
    'reviewedBy': {
      '@type': 'Person',
      '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel`,
      'name': 'Dr. Kartik Patel',
    },
    'medicalAudience': { '@type': 'MedicalAudience', 'audienceType': 'Patient' },
    'image': article.image,
    'publisher': {
      '@type': 'Organization',
      '@id': `${PUBLIC_SITE_URL}/#organization`,
      'name': PRACTICE.name,
      'logo': {
        '@type': 'ImageObject',
        'url': BRAND_LOGO_IMAGE,
      },
    },
  };
};

// ============================================================================
// HELPER: COMBINE SCHEMAS
// ============================================================================

/**
 * Combine multiple schemas into an array for SEO component
 * 
 * @param {...object} schemas - Schema objects to combine
 * @returns {Array} Array of non-null schemas
 * 
 * @example
 * const allSchemas = combineSchemas(
 *   getOrganizationLD(),
 *   getFAQPageLD(faqs),
 *   getBreadcrumbLD(breadcrumbs)
 * );
 * 
 * <SEO structuredData={allSchemas} />
 */
export const combineSchemas = (...schemas) => {
  return schemas.filter(schema => schema !== null && schema !== undefined);
};

// ============================================================================
// SEDATION CLUSTER (service + speakable WebPage)
// ============================================================================

/**
 * MedicalProcedure + Service for a sedation page, with the areas served named
 * so the page is eligible for local "near me" surfaces.
 */
export const getSedationServiceLD = ({ name, description, path, priceFrom, areasServed = [] }) => ({
  '@context': 'https://schema.org',
  '@type': ['MedicalProcedure', 'Service'],
  '@id': `${PUBLIC_SITE_URL}${path}#service`,
  'name': name,
  'description': description,
  'url': `${PUBLIC_SITE_URL}${path}`,
  'procedureType': 'https://schema.org/NoninvasiveProcedure',
  'bodyLocation': { '@type': 'AnatomicalStructure', 'name': 'Mouth' },
  'howPerformed': 'A sedative is given through a cannula in the hand or arm by a qualified sedationist. The patient remains conscious and responsive but deeply relaxed, and is monitored continuously throughout the appointment.',
  'preparation': 'An in-person sedation assessment is carried out beforehand to review medical history and medication. A responsible adult escort is required on the day of treatment.',
  'followup': 'Monitored recovery of 20–30 minutes at the practice, then home with the escort. No driving, work or important decisions for the rest of the day.',
  'provider': { '@id': `${PUBLIC_SITE_URL}/#dentist` },
  'performedBy': { '@id': `${PUBLIC_SITE_URL}/#person-dr.-kartik-patel` },
  'areaServed': [
    { '@type': 'City', 'name': 'Acton, London' },
    ...areasServed.map((a) => ({ '@type': 'City', 'name': `${a.name}, London ${a.postcode}` })),
  ],
  'offers': {
    '@type': 'Offer',
    'priceCurrency': 'GBP',
    'price': priceFrom,
    'priceSpecification': {
      '@type': 'PriceSpecification',
      'priceCurrency': 'GBP',
      'minPrice': priceFrom,
      'description': 'From price for the first hour of IV sedation. Dental treatment is quoted separately and every patient receives a fixed written quote after an in-person assessment.',
    },
    'availableAtOrFrom': { '@id': `${PUBLIC_SITE_URL}/#dentist` },
  },
});

/** WebPage with a real speakable selector — the direct-answer block. */
export const getSpeakableWebPageLD = ({ name, description, path }) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${PUBLIC_SITE_URL}${path}#webpage`,
  'url': `${PUBLIC_SITE_URL}${path}`,
  'name': name,
  'description': description,
  'inLanguage': 'en-GB',
  'isPartOf': { '@id': `${PUBLIC_SITE_URL}/#website` },
  'about': { '@id': `${PUBLIC_SITE_URL}${path}#service` },
  'speakable': {
    '@type': 'SpeakableSpecification',
    'cssSelector': ['h1', '.direct-answer'],
  },
});

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

export default {
  // Core Schema Builders (Prompt 5)
  getOrganizationLD,
  getWebSiteLD,
  getDentistClinicLD,
  getPersonClinicianLD,
  drKartikPatel,
  
  // Medical Schemas (Prompt 6)
  getMedicalConditionLD,
  getMedicalProcedureLD,
  
  // FAQ Schema (Prompt 7)
  getFAQPageLD,
  
  // Breadcrumb Schema (Prompt 8)
  getBreadcrumbLD,
  
  // Speakable Schema (Prompt 9)
  getSpeakableLD,
  
  // Article Schema (Prompt 10)
  getMedicalWebPageLD,
  
  // Supporting Schemas
  getLocalBusinessLD,
  getMedicalServiceLD,
  getAggregateRatingLD,
  
  // Utilities
  combineSchemas,
};
